/**
 * React Query Mutations for Agent Configuration
 *
 * Mutation hooks for creating, updating, and deleting agents
 */

import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { agentApi, AgentConfigUpdateResponse } from '../api';
import type { HttpError } from '../api/client';
import { QUERY_KEYS } from './useAgentsQuery';
import type { AgentConfiguration, AgentConfigResponse } from '@/types';

/**
 * Create new agent configuration
 */
export function useCreateAgentMutation(): UseMutationResult<
  AgentConfigResponse,
  Error,
  { config: AgentConfiguration }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ config }) => agentApi.create(config),
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentsList() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentConfig(data.agent_type) });
    },
  });
}

export interface AgentConfigConflictError {
  type: 'conflict';
  currentVersion: number;
  expectedVersion: number;
  message: string;
}

export function isConflictError(err: unknown): err is AgentConfigConflictError {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as AgentConfigConflictError).type === 'conflict'
  );
}

/**
 * Update agent configuration
 */
export function useUpdateAgentMutation(): UseMutationResult<
  AgentConfigUpdateResponse,
  Error | AgentConfigConflictError,
  { agentType: string; config: Partial<AgentConfiguration>; description?: string; expectedVersion?: number }
> {
  const queryClient = useQueryClient();

  return useMutation({
    retry: false,
    mutationFn: async ({ agentType, config, description, expectedVersion }) => {
      try {
        return await agentApi.update(agentType, config, description, expectedVersion);
      } catch (err: unknown) {
        const httpErr = err as HttpError;
        if (httpErr.statusCode === 409) {
          const detail = (httpErr.responseData as any)?.detail ?? {};
          const conflictErr: AgentConfigConflictError = {
            type: 'conflict',
            currentVersion: detail.current_version ?? 0,
            expectedVersion: detail.expected_version ?? 0,
            message: detail.message ?? '配置已被其他管理员修改',
          };
          throw conflictErr;
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentConfig(data.agent_type) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentsList() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentVersions(data.agent_type) });
    },
  });
}

/**
 * Deactivate (soft-delete) an agent configuration.
 * Backend sets is_active=false; config remains in DB for potential reactivation.
 */
export function useDeactivateAgentMutation(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentType: string) => agentApi.delete(agentType),
    onSuccess: (_, agentType) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentsList() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentConfig(agentType) });
    },
  });
}

/** @deprecated Use useDeactivateAgentMutation instead */
export const useDeleteAgentMutation = useDeactivateAgentMutation;

/**
 * Reactivate a deactivated agent
 */
export function useReactivateAgentMutation(): UseMutationResult<
  AgentConfigResponse,
  Error,
  string
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentType: string) => agentApi.reactivate(agentType),
    onSuccess: (_, agentType) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentsList() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentConfig(agentType) });
    },
  });
}

/**
 * Activate specific version
 */
export function useActivateVersionMutation(): UseMutationResult<
  AgentConfigResponse,
  Error,
  { agentType: string; version: number; reason?: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentType, version, reason }) => agentApi.activateVersion(agentType, version, reason),
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentConfig(data.agent_type) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentVersions(data.agent_type) });
    },
  });
}

/**
 * Invalidate agent cache
 */
export function useInvalidateCacheMutation(): UseMutationResult<void, Error, string | undefined> {
  return useMutation({
    mutationFn: (agentType?: string) => agentApi.invalidateCache(agentType),
  });
}

/**
 * Export agent configurations
 */
export function useExportConfigsMutation(): UseMutationResult<Blob, Error, string[] | undefined> {
  return useMutation({
    mutationFn: (agentTypes?: string[]) => agentApi.export(agentTypes),
  });
}

/**
 * Import agent configurations
 */
export function useImportConfigsMutation(): UseMutationResult<
  any,
  Error,
  { file: File; mode?: 'create-only' | 'overwrite' | 'merge'; skipErrors?: boolean }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, mode, skipErrors }) => agentApi.import(file, mode, skipErrors),
    onSuccess: () => {
      // Invalidate all agent queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agents });
    },
  });
}
